import React, { useEffect, useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Container, Button } from '../Layout';
import { gridContainerStyle, columnStyle } from './styles';
import { Divider } from '@mui/material';
import i18n from '../../helpers/i18n';
import { createDevice, getEmployees, getRepaList } from './CreateDeviceContent.queries';
import { useFormik } from 'formik';
import { validationSchema } from './validationSchema';
import { RestrictionSection, AuthorizedCollaboratorsSection, ControllerSection, DeviceSection, ButtonDisplaySection, DeviceForReceiptSection } from '../CreateEditSections/';
import { deviceTypes, filterTypes } from '../../helpers/consts/deviceConstants';
import { useNavigate } from 'react-router-dom';
import DeviceCreatedAlert from '../DeviceCreatedAlert/DeviceCreatedAlert';
import { useError } from '../SnackbarError/ErrorContext';
import { useDevicesPermissions } from '../../hooks/useDevicesPermissions';


const CreateDeviceContent = () => {
    const queryClient = useQueryClient();
    
    const [offset, setOffset] = useState(0);
    const [byName, setByName] = useState('');
    const [employees, setEmployees] = useState([]); 
    const [locations, setLocations] = useState([]);
    const [cnpjs, setCnpjs] = useState([]);

    const navigate = useNavigate();

    const [isOpenCreateDevice, setIsOpenCreateDevice] = useState(false);
    const [activationKey, setActivationKey] = useState('');
    const [company, setCompany] = useState('');
    const [deviceType, setDeviceType] = useState('');

    const { showError } = useError();

    const [hasCnpjSet, setHasCnpjSet] = useState(false);

    const { permissions, locations: locationsData, cnpjs: cnpjsData, company: companyCode, totalEmployees, isLoadingPermissions } = useDevicesPermissions();
    
    const [duplicateNameError, setDuplicateNameError] = useState('');
    
    const { data: dataEmployees, isLoading: isLoadingEmployees } = useQuery({
        queryKey: ['funcionarios', offset, byName, totalEmployees],
        queryFn: () => getEmployees({ limit: 50, offset, byName, totalEmployees }),
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false,
        enabled: typeof totalEmployees === 'number'
    });

    const { data: repaListData, isLoading: isLoadingRepaList } = useQuery({
        queryKey: ['repaList', companyCode],
        queryFn: () => getRepaList(companyCode),
        refetchOnWindowFocus: false,
        retry: false,
        enabled: !!companyCode
    });

    const mutationCreateDevice = useMutation({
        mutationFn: createDevice,
        onSuccess: (data) => {
            if (!data.response.result && data.response.errorType) {
                let errorMsg = i18n.t('pages.errors.device.createDefault');
                
                // Tratamento de erros específicos que vem do pw retornando 200 como status
                switch (data.response.errorType) {
                    case 'existsName':
                        setDuplicateNameError(i18n.t('pages.errors.device.existsName'));
                        showError(i18n.t('pages.errors.device.existsNameSnackbar'));
                        return;
                    case 'emptyLocation':
                        errorMsg = i18n.t('pages.errors.device.emptyLocation');
                        break;
                    default:
                        errorMsg = i18n.t('pages.errors.device.createWithType', { errorType: data.response.errorType });
                }
                
                showError(errorMsg);
                return;
            }

            setActivationKey(data.response.activationKey);
            setCompany(data.response.company);
            setDeviceType(data.response.deviceType);
            setIsOpenCreateDevice(true);
        },
        onError: (e) => {
            let errorType = e.message.replace('Error: ', '');
            switch (errorType) {
                case 'existsName':
                    setDuplicateNameError(i18n.t('pages.errors.device.existsName'));
                    showError(i18n.t('pages.errors.device.existsNameSnackbar'));
                    break;
                case 'emptyLocation':
                    showError(i18n.t('pages.errors.device.emptyLocation'));
                    break;
                default:
                    showError(i18n.t('pages.errors.device.createDefault'));
            }
        },
    });

    const handleSubmit = async (values) => {
        if (values.employees.length === 0 && values.locations.length === 0) {
            showError(i18n.t('pages.errors.device.noEmployeeSelected'));
            return;
        }
        // Limpa o erro de nome duplicado ao tentar submeter novamente
        setDuplicateNameError('');
        
        // Ajusta a flag antes do envio conforme o tipo do dispositivo
        const adjustedValues = values.deviceType === 'ahgora_online_device'
            ? { ...values, only_with_location: false }
            : values;
        
        mutationCreateDevice.mutate({deviceData: adjustedValues, queryClient});
    };

    const formik = useFormik({
        initialValues: {
            deviceType: '',
            cnpj: '',
            name: '',
            timezone: 'America/Sao_Paulo',
            activationKey: '',
            active: false,
            expirationDate: false,
            startPeriod: null,
            endPeriod: null,

            collaboratorsType: 'by_employee',
            employees: [],
            locations: [],

            //Controller
            enable_password_punch: false, //Ativar batida por matrícula e senha
            break_device: false, //Ativar dispositivo de pausa NR17
            is_repa: false, //Ativa dispositivo com o fluxo REP-A
            enableRepA: false, //Switch REP-A no DeviceSection
            enable_auto_update: false, //Ativar atualização automática (Tablet Ahgora)
            default_computer: false, //Ativar batida online padrão por link
            timezone_filter: false,  //Ativar fuso horário como filtro
            triple_validation: false, //Ativar validação tripla para múltiplos usuários
            enable_punches_logs: false, //ativar logs de batidas
            enable_punch_backup: false, //Ativar download de arquivo com batida de ponto (Solicitar donwload do arquivo)
            enable_punch_by_manager: false, // Habilita que dispositivos Multi tenham a opção de registro de ponto pelo gestor
            save_photo_on_punch: false, //Habilitar captura de foto ao realizar registro de ponto
            blink: false,
            smile: false,
            head_movement: false,
            enable_facial_recognition_flow: false, //Ativação Reconhecimento Facial

            //DeviceForReceipt
            device_for_receipt_flag_multi: false,

            //Restriction
            online_only: false,
            auto_timezone: false,
            only_with_location: false,
            only_inside_geofence: false,
            range_ip: false,
            ips: [], //Ip Range '123.123.123.23, 123.123.123.24'

            //ButtonDisplay - Flags para ocultar botões no app Multi
            hide_password_config_access: false,
            hide_reconfigure_multi: false,
            hide_punch_receipt_display_time: false,

            useSelectAllEmployees: false,
        },
        validationSchema,
        onSubmit: handleSubmit
    });

    const initializedLocationFlagRef = useRef(false);

    useEffect(() => {
        if (initializedLocationFlagRef.current) return;
        if (permissions?.batida_por_geolocalizacao_obrigatoria) {
            formik.setFieldValue('only_with_location', true);
            initializedLocationFlagRef.current = true;
        }
    }, [permissions, formik]);

    useEffect(() => {
        if (locationsData || cnpjsData) {
            let mappedLocations = locationsData;

            if (typeof mappedLocations === 'object' && !Array.isArray(mappedLocations)) {
                mappedLocations = Object.values(mappedLocations);
            }

            const locationArray = (mappedLocations || []).map(location => ({ value: location, title: location }));
            const cnpjArray = (cnpjsData || []).map(cnpjItem => ({ 
                value: cnpjItem.cnpj, 
                title: `${cnpjItem.cnpj} - ${cnpjItem.razao_social}` 
            }));

            setLocations(locationArray);
            setCnpjs(cnpjArray);

            if (!hasCnpjSet && !formik.values.cnpj && cnpjArray.length > 0) {
                formik.setFieldValue('cnpj', cnpjArray[0].value);
                setHasCnpjSet(true);
            }
        }
    }, [locationsData, cnpjsData, formik, hasCnpjSet]);

    useEffect(() => {

        if (dataEmployees?.data) {
            const newEmployees = dataEmployees.data.map(employee => ({
                value: employee.dados.matricula,
                title: `${employee.dados.matricula} | ${employee.dados.nome}`
            }));
    
            setEmployees(prevEmployees => {
                const uniqueEmployees = newEmployees.filter(newEmployee =>
                    !prevEmployees.some(existingEmployee => existingEmployee.value === newEmployee.value)
                );
                return [...prevEmployees, ...uniqueEmployees];
            });
        }
    }, [dataEmployees, byName]); 

    const handleScroll = (event) => {
        if (isLoadingEmployees || byName) return; 
        const { scrollHeight, scrollTop, clientHeight } = event.target;
        if (scrollTop + clientHeight >= scrollHeight - 5) {
            setOffset(prevOffset => prevOffset + 1);
        }
    };

    const handleFilterSearch = (searchText) => {
        setByName(searchText); 
    };

    function handleCreatedDevice() {
        setIsOpenCreateDevice(false);
        navigate('/');
    }

    return (
        <Container>
            <DeviceCreatedAlert isOpen={isOpenCreateDevice} onClose={handleCreatedDevice} company={company} deviceType={deviceType} activationKey={activationKey} />
            <form style={{ width: '100%' }} onSubmit={formik.handleSubmit}>
                <div style={gridContainerStyle}>
                    <div style={columnStyle}>
                        <DeviceSection
                            formik={formik}
                            cnpjs={cnpjs}
                            deviceTypes={deviceTypes}
                            isLoadingLocationsAndCnpjs={isLoadingPermissions}
                            duplicateNameError={duplicateNameError}
                            onNameChange={() => setDuplicateNameError('')}
                            permissions={permissions}
                            repaList={repaListData || []}
                            isLoadingRepaList={isLoadingRepaList} />

                        <AuthorizedCollaboratorsSection
                            formik={formik}
                            employees={employees} 
                            locations={locations}
                            isLoadingLocationsAndCnpjs={isLoadingPermissions}
                            handleFilterSearch={handleFilterSearch}
                            handleScroll={handleScroll}
                            isLoadingEmployees={isLoadingEmployees}
                            filterTypes={filterTypes}
                        />
                    </div>

                    <div style={columnStyle}>
                        <DeviceForReceiptSection formik={formik} company={companyCode} permissions={permissions} />

                        <ControllerSection formik={formik} permissions={permissions} />

                        {permissions?.permite_restricao_batida && (
                            <RestrictionSection formik={formik} permissions={permissions}/>
                        )}

                        <ButtonDisplaySection formik={formik} permissions={permissions}/>
                    </div>
                </div>
                <Divider style={{ marginBottom: 16 }} />
                <Button type="outlined"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/');
                    }}
                >{i18n.t('pages.newDevice.cancel')}</Button>
                <Button type="submit">{i18n.t('pages.newDevice.createDevice')}</Button>
            </form>
        </Container>
    );
};

export default CreateDeviceContent;
